import { useState, type FormEvent } from "react";
import { useAuth } from "@/lib/auth-store";
import { probeHealthAndAuth } from "@/lib/api-client";
import { useBranding } from "@/lib/use-branding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, KeyRound } from "lucide-react";

export function LoginPage() {
  const setToken = useAuth((s) => s.setToken);
  const { name: brandName } = useBranding();
  const [tokenInput, setTokenInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (tokenInput.trim().length === 0) {
      setError("Please paste your admin token.");
      return;
    }
    setSubmitting(true);
    try {
      const probe = await probeHealthAndAuth(tokenInput.trim());
      if (!probe.workerOk) {
        setError(
          import.meta.env.PROD
            ? "Cannot reach the backend worker. Check the Cloudflare Worker is deployed and the dashboard's API URL is set."
            : "Worker is unreachable. Is `pnpm dev:worker` running on port 8787?",
        );
        return;
      }
      if (!probe.dbOk) {
        setError("Worker is up but the database is unreachable. Check DATABASE_URL.");
        return;
      }
      if (probe.tokenAccepted === false) {
        setError(
          import.meta.env.PROD
            ? "This token is not valid. Check the ADMIN_TOKEN set on the Cloudflare Worker."
            : "This token is not valid. Double-check `apps/worker/.dev.vars`.",
        );
        return;
      }
      // probe.tokenAccepted is true or "unknown" (e.g. no protected route hit yet).
      setToken(tokenInput.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex h-full items-center justify-center bg-slate-50 p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4" />
            {brandName} - Sign in
          </CardTitle>
          <CardDescription>
            Paste your admin token from{" "}
            <code className="rounded bg-slate-100 px-1 text-[11px]">
              apps/worker/.dev.vars
            </code>
            . It is stored locally in your browser and never sent anywhere else
            than your own Worker.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-3" onSubmit={onSubmit}>
            <Input
              type="password"
              autoFocus
              autoComplete="off"
              placeholder="sk-... or iXEis08..."
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              disabled={submitting}
            />
            {error ? (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {error}
              </div>
            ) : null}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {submitting ? "Verifying..." : "Sign in"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
